// Governance contracts — audit trail, Control Log, patient access log, exports, signed links, privacy documents, the DSAR
// register and, from Sprint 2, consent capture (modules-and-api.md §5.5, §2.4, §2.8; TEN-52, TEN-58..68, TEN-76, TEN-77,
// PAT-71..PAT-75, INT-75e; BR-GOVERNANCE-001..027).
// Nothing here ever carries a storage key, a hash of a secret or a classified value.
import { z } from "zod";
import { CONTROL_LOG_ACTIONS, type ControlLogAction } from "./auth";
import { InstantSchema, LocalDateSchema, UlidSchema, isValidSaId } from "./primitives";
import type { SpiClass } from "./spi";
import { PATIENT_STATUSES } from "./status-machines";

// --- Enumerations (the values the tenant schema CHECKs accept) ---------------------------------------------------------------

export const AUDIT_ACTIONS = [
  "Create", "Update", "Delete", "Void", "View", "Export", "Print",
  "Login", "LoginFailed", "Logout", "Lock", "Unlock",
  "PasswordReset", "TotpChange", "PinChange", "PinLock",
  "RoleChange", "BranchAssign", "SettingsChange", "FlagChange",
  "SupportGrant", "Invite", "Deactivate", "Reactivate",
  "BranchSwitch", "HandoverEnter", "HandoverExit", "RecoveryCodeUse",
] as const;
export type AuditActionCode = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_SOURCES = ["UI", "API", "Import", "AccountingSync", "Cron", "Workflow"] as const;
export type AuditSourceCode = (typeof AUDIT_SOURCES)[number];
export const AUDIT_ACTOR_TYPES = ["User", "System", "PlatformAdmin", "FranchisorUser"] as const;
export const CONTROL_OUTCOMES = ["Authorised", "Refused", "Locked"] as const;
export type ControlOutcome = (typeof CONTROL_OUTCOMES)[number];
export const CREDENTIAL_TYPES = ["PIN", "WebAuthn"] as const;
/** Every action a Control Log row may carry: the stored enum of auth.ts, the D20 administrative actions included (migration 0001). */
export const CONTROL_LOG_ROW_ACTIONS = CONTROL_LOG_ACTIONS;
export type ControlLogRowAction = ControlLogAction;
export const ACCESS_TYPES = ["Profile", "Clinical", "Documents", "Export", "Print"] as const;
export type AccessType = (typeof ACCESS_TYPES)[number];
export const PRIVACY_DOC_TYPES = ["PrivacyNotice", "ConsentText"] as const;
export type PrivacyDocType = (typeof PRIVACY_DOC_TYPES)[number];
export const DSAR_TYPES = ["Access", "Correction", "Erasure", "Objection"] as const;
export type DsarType = (typeof DSAR_TYPES)[number];
export const DSAR_VIA = ["InStore", "Email", "Phone", "Post", "Online"] as const;
export type DsarVia = (typeof DSAR_VIA)[number];
export const DSAR_STATUSES = ["Open", "Fulfilled", "Refused", "Deferred"] as const;
export type DsarStatus = (typeof DSAR_STATUSES)[number];
export const EXPORT_VIEWS = ["audit", "by-record", "by-user"] as const;
export type ExportView = (typeof EXPORT_VIEWS)[number];

// --- Limits (BR-GOVERNANCE-005, 007, 011, 015) ------------------------------------------------------------------------------

/** A staff signed link lives at most this long (TEN-70, §2.8). */
export const SIGNED_LINK_TTL_SECONDS = 15 * 60;
/** An on-request CSV holds at most this many rows; a larger set is 422 `range` `too_many_rows` (BR-GOVERNANCE-015). */
export const EXPORT_ROW_CAP = 50_000;
export const PRIVACY_BODY_MAX_LENGTH = 20_000;
/** Without `from`/`to` the Control Log shows the last 30 days (UC-GOVERNANCE-005 step 2). */
export const CONTROL_LOG_DEFAULT_DAYS = 30;
/** POPIA/PAIA: a data-subject request is answered within 30 days; `dueAt` is derived, never stored. */
export const DSAR_RESPONSE_DAYS = 30;
/**
 * Nightly sealing retention lock when the organisation's log retention is `indefinite` (D41): a bounded, renewable lock of
 * ten years rather than the storage sentinel — a lock that could never lapse would make deletion impossible, and deletion
 * of sealed evidence stays a deliberate platform act. The lock is renewed by the sealing of later days, never shortened.
 */
export const SEALED_INDEFINITE_YEARS = 10;

// --- Keyset cursors (BR-GOVERNANCE-007) -------------------------------------------------------------------------------------

export const GOVERNANCE_COLLECTIONS = ["audit", "by-record", "by-user", "control-log", "patient-access-log", "privacy-documents", "dsar-requests", "consents"] as const;
export type GovernanceCollection = (typeof GOVERNANCE_COLLECTIONS)[number];

export interface GovernanceCursor { collection: GovernanceCollection; at: string; id: string }

const b64url = (s: string): string => btoa(unescape(encodeURIComponent(s))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromB64url = (s: string): string | null => {
  try {
    const padded = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
    return decodeURIComponent(escape(atob(padded)));
  } catch {
    return null;
  }
};

/** Encodes the keyset position (time, id) of the last item of a page, tagged with its collection. */
export function encodeGovernanceCursor(collection: GovernanceCollection, at: string, id: string): string {
  return b64url(JSON.stringify([collection, at, id]));
}

/** Decodes a cursor; null unless the system issued it for this collection (a cursor of another collection is refused). */
export function decodeGovernanceCursor(cursor: string, collection: GovernanceCollection): GovernanceCursor | null {
  const text = fromB64url(cursor);
  if (text === null) return null;
  let parsed: unknown;
  try { parsed = JSON.parse(text); } catch { return null; }
  if (!Array.isArray(parsed) || parsed.length !== 3) return null;
  const [c, at, id] = parsed as unknown[];
  if (c !== collection || typeof at !== "string" || typeof id !== "string" || at.length === 0 || id.length === 0) return null;
  return { collection, at, id };
}

const cursorSchema = (collection: GovernanceCollection) =>
  z.string().max(512).refine((c) => decodeGovernanceCursor(c, collection) !== null, { message: "cursor was not issued by the system for this collection", params: { code: "invalid_cursor" } });

const Limit = z.coerce.number().int().min(1).max(200).default(50);
const Flag = z.enum(["true", "false"]);
const OpaqueId = z.string().trim().min(1).max(64);

/** `from`/`to` are instants; the range includes both ends; `to` before `from` is 422 on `to` (BR-GOVERNANCE-008). */
const Range = { from: InstantSchema.optional(), to: InstantSchema.optional() };
function rangeCheck(v: { from?: string | undefined; to?: string | undefined }, ctx: z.RefinementCtx): void {
  if (v.from !== undefined && v.to !== undefined && Date.parse(v.to) < Date.parse(v.from)) {
    ctx.addIssue({ code: "custom", path: ["to"], message: "to must not be earlier than from", params: { code: "before_from" } });
  }
}
function required(v: Record<string, unknown>, ctx: z.RefinementCtx, ...fields: string[]): void {
  for (const f of fields) if (v[f] === undefined || v[f] === "") ctx.addIssue({ code: "custom", path: [f], message: `${f} is required`, params: { code: "required" } });
}

// --- Audit trail (UC-GOVERNANCE-001..004) -----------------------------------------------------------------------------------

const AuditFilterShape = {
  userId: UlidSchema.optional(),
  entityType: z.string().trim().min(1).max(64).optional(),
  entityId: OpaqueId.optional(),
  action: z.enum(AUDIT_ACTIONS).optional(),
  source: z.enum(AUDIT_SOURCES).optional(),
  branchId: UlidSchema.optional(),
};

export const AuditQuerySchema = z.object({ ...AuditFilterShape, ...Range, limit: Limit, cursor: cursorSchema("audit").optional(), count: Flag.optional() }).superRefine(rangeCheck);
export type AuditQuery = z.infer<typeof AuditQuerySchema>;

export const AuditByRecordQuerySchema = z.object({
  entityType: z.string().trim().max(64).optional(),
  entityId: z.string().trim().max(64).optional(),
  ...Range, limit: Limit, cursor: cursorSchema("by-record").optional(), count: Flag.optional(),
}).superRefine((v, ctx) => { required(v, ctx, "entityType", "entityId"); rangeCheck(v, ctx); })
  .transform((v) => ({ ...v, entityType: v.entityType!, entityId: v.entityId! }));
export type AuditByRecordQuery = z.infer<typeof AuditByRecordQuerySchema>;

export const AuditByUserQuerySchema = z.object({
  userId: UlidSchema.optional(),
  ...Range, limit: Limit, cursor: cursorSchema("by-user").optional(), count: Flag.optional(),
}).superRefine((v, ctx) => { required(v, ctx, "userId"); rangeCheck(v, ctx); })
  .transform((v) => ({ ...v, userId: v.userId! }));
export type AuditByUserQuery = z.infer<typeof AuditByUserQuerySchema>;

/** POST /audit/export — the filters of the view being exported (UC-GOVERNANCE-004). */
export const AuditExportRequestSchema = z.strictObject({
  view: z.enum(EXPORT_VIEWS).default("audit"),
  ...AuditFilterShape,
  ...Range,
}).superRefine((v, ctx) => {
  rangeCheck(v, ctx);
  if (v.view === "by-user" && !v.userId) ctx.addIssue({ code: "custom", path: ["userId"], message: "userId is required for a by-user export", params: { code: "required" } });
  if (v.view === "by-record") {
    if (!v.entityType) ctx.addIssue({ code: "custom", path: ["entityType"], message: "entityType is required for a by-record export", params: { code: "required" } });
    if (!v.entityId) ctx.addIssue({ code: "custom", path: ["entityId"], message: "entityId is required for a by-record export", params: { code: "required" } });
  }
});
export type AuditExportRequest = z.infer<typeof AuditExportRequestSchema>;

export const ActorViewSchema = z.object({
  type: z.enum(AUDIT_ACTOR_TYPES),
  /** `user:<id>`, `platform_admin:<id>`, `api_client:<id>` or `system:<job>` — never empty (TEN-63). */
  id: z.string().min(1),
  displayName: z.string().nullable(),
  controlId: z.number().int().nullable(),
});
export type ActorView = z.infer<typeof ActorViewSchema>;

export const BranchRefSchema = z.object({ id: UlidSchema, code: z.string() });
export const UserRefSchema = z.object({ id: UlidSchema, controlId: z.number().int(), displayName: z.string() });
export type UserRef = z.infer<typeof UserRefSchema>;

export const AuditDiffSchema = z.object({
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  /** Classified fields set to null for this reader (BR-GOVERNANCE-003). */
  _redacted: z.array(z.string()).optional(),
});
export type AuditDiff = z.infer<typeof AuditDiffSchema>;

export const AuditEntryViewSchema = z.object({
  kind: z.literal("audit"),
  id: UlidSchema,
  occurredAt: InstantSchema,
  actor: ActorViewSchema,
  action: z.enum(AUDIT_ACTIONS),
  entityType: z.string(),
  entityId: z.string().nullable(),
  branch: BranchRefSchema.nullable(),
  source: z.enum(AUDIT_SOURCES),
  requestId: z.string().nullable(),
  sessionId: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  impersonation: z.boolean(),
  actingOrgId: z.string().nullable(),
  diff: AuditDiffSchema.nullable(),
  /** The Control Log authoriser of the same request, when the change was a step-up (UC-GOVERNANCE-002). */
  authorisedBy: z.object({ controlId: z.number().int(), displayName: z.string() }).nullable(),
});
export type AuditEntryView = z.infer<typeof AuditEntryViewSchema>;

export const AccessEntryViewSchema = z.object({
  kind: z.literal("access"),
  id: UlidSchema,
  occurredAt: InstantSchema,
  actor: ActorViewSchema,
  user: UserRefSchema.nullable(),
  patientId: z.string(),
  accessType: z.enum(ACCESS_TYPES),
  purpose: z.string().nullable(),
  branch: BranchRefSchema.nullable(),
  sessionId: z.string().nullable(),
  requestId: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type AccessEntryView = z.infer<typeof AccessEntryViewSchema>;

export const UserTimelineEntrySchema = z.union([AuditEntryViewSchema, AccessEntryViewSchema]);
export type UserTimelineEntry = z.infer<typeof UserTimelineEntrySchema>;

export const ExportResponseSchema = z.object({
  exportId: UlidSchema,
  fileName: z.string(),
  rowCount: z.number().int(),
  sha256: z.string(),
  /** `/api/v1/files/{token}` — opaque; never the storage key (BR-GOVERNANCE-005). */
  downloadUrl: z.string(),
  expiresAt: InstantSchema,
});
export type ExportResponse = z.infer<typeof ExportResponseSchema>;

// --- Control Log (UC-GOVERNANCE-005, 006) ----------------------------------------------------------------------------------

const ControlLogFilterShape = {
  branchId: UlidSchema.optional(),
  actingUserId: UlidSchema.optional(),
  authorisingUserId: UlidSchema.optional(),
  action: z.enum(CONTROL_LOG_ROW_ACTIONS).optional(),
  recordType: z.string().trim().min(1).max(64).optional(),
  outcome: z.enum(CONTROL_OUTCOMES).optional(),
};

export const ControlLogQuerySchema = z.object({ ...ControlLogFilterShape, ...Range, limit: Limit, cursor: cursorSchema("control-log").optional(), count: Flag.optional(), totals: Flag.optional() }).superRefine(rangeCheck);
export type ControlLogQuery = z.infer<typeof ControlLogQuerySchema>;

export const ControlLogExportRequestSchema = z.strictObject({ ...ControlLogFilterShape, ...Range }).superRefine(rangeCheck);
export type ControlLogExportRequest = z.infer<typeof ControlLogExportRequestSchema>;

export const ControlLogEntryViewSchema = z.object({
  id: UlidSchema,
  occurredAt: InstantSchema,
  branch: BranchRefSchema.nullable(),
  action: z.enum(CONTROL_LOG_ROW_ACTIONS),
  recordType: z.string(),
  recordId: z.string(),
  documentNumber: z.string().nullable(),
  actingUser: UserRefSchema.nullable(),
  /** Null on a Refused or Locked attempt: nobody authorised it (BR-GOVERNANCE-010). */
  authorisingUser: UserRefSchema.nullable(),
  controlIdEntered: z.number().int(),
  credentialType: z.enum(CREDENTIAL_TYPES),
  discountPercentBp: z.number().int().nullable(),
  discountValueCents: z.number().int().nullable(),
  priceBeforeCents: z.number().int().nullable(),
  priceAfterCents: z.number().int().nullable(),
  varianceAmountCents: z.number().int().nullable(),
  reason: z.string().nullable(),
  outcome: z.enum(CONTROL_OUTCOMES),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  sessionId: z.string().nullable(),
  requestId: z.string().nullable(),
});
export type ControlLogEntryView = z.infer<typeof ControlLogEntryViewSchema>;

const MoneyTotal = z.object({ count: z.number().int(), valueCents: z.number().int() });
export const ControlLogTotalsLineSchema = z.object({
  user: UserRefSchema.nullable(),
  userId: UlidSchema,
  discounts: MoneyTotal,
  voids: MoneyTotal,
  creditNotes: MoneyTotal,
  refunds: MoneyTotal,
  /** Attempts Refused or Locked (BR-GOVERNANCE-010). */
  refused: z.number().int(),
});
export type ControlLogTotalsLine = z.infer<typeof ControlLogTotalsLineSchema>;

export const ControlLogPageSchema = z.object({
  items: z.array(ControlLogEntryViewSchema),
  nextCursor: z.string().optional(),
  totalCount: z.number().int().optional(),
  /** Only with `?totals=true`; over the whole filtered set. */
  totals: z.array(ControlLogTotalsLineSchema).optional(),
});

// --- Patient access log (UC-GOVERNANCE-007) ---------------------------------------------------------------------------------

export const PatientAccessLogQuerySchema = z.object({
  patientId: OpaqueId.optional(),
  userId: UlidSchema.optional(),
  accessType: z.enum(ACCESS_TYPES).optional(),
  branchId: UlidSchema.optional(),
  ...Range, limit: Limit, cursor: cursorSchema("patient-access-log").optional(), count: Flag.optional(),
}).superRefine((v, ctx) => {
  rangeCheck(v, ctx);
  if (!v.patientId && !v.userId) ctx.addIssue({ code: "custom", path: ["patientId"], message: "patientId or userId is required", params: { code: "one_of_required" } });
});
export type PatientAccessLogQuery = z.infer<typeof PatientAccessLogQuerySchema>;

// --- Privacy documents (UC-GOVERNANCE-008) ----------------------------------------------------------------------------------

const ValidLocalDate = LocalDateSchema.refine((d) => {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, day!));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m! - 1 && dt.getUTCDate() === day;
}, "not a calendar date");

export const PrivacyDocumentsQuerySchema = z.object({
  docType: z.enum(PRIVACY_DOC_TYPES).optional(),
  limit: Limit, cursor: cursorSchema("privacy-documents").optional(), count: Flag.optional(),
});
export type PrivacyDocumentsQuery = z.infer<typeof PrivacyDocumentsQuerySchema>;

export const PrivacyDocumentPublishSchema = z.strictObject({
  docType: z.enum(PRIVACY_DOC_TYPES),
  bodyText: z.string().min(1).max(PRIVACY_BODY_MAX_LENGTH),
  /** Defaults to the publish date in the organisation's timezone; may not precede the previous version's (BR-GOVERNANCE-011). */
  effectiveFrom: ValidLocalDate.optional(),
});
export type PrivacyDocumentPublish = z.infer<typeof PrivacyDocumentPublishSchema>;

export const PrivacyDocumentViewSchema = z.object({
  id: UlidSchema,
  docType: z.enum(PRIVACY_DOC_TYPES),
  version: z.number().int(),
  bodyText: z.string(),
  effectiveFrom: LocalDateSchema,
  publishedAt: InstantSchema.nullable(),
  publishedBy: UserRefSchema.nullable(),
  isCurrent: z.boolean(),
  createdAt: InstantSchema,
});
export type PrivacyDocumentView = z.infer<typeof PrivacyDocumentViewSchema>;

export const CONSENT_STATUSES = ["Current", "Outdated", "Withdrawn", "NotGiven"] as const;
export type ConsentStatus = (typeof CONSENT_STATUSES)[number];

/**
 * How a consent record reads against the organisation's current consent text version (R-15, BR-GOVERNANCE-011): a consent
 * recorded on an earlier version is `Outdated` and its patient is asked again at the next visit.
 */
export function consentStatus(record: { textVersion: number; isGranted: boolean; withdrawnAt: string | null }, currentTextVersion: number | null): ConsentStatus {
  if (record.withdrawnAt) return "Withdrawn";
  if (!record.isGranted) return "NotGiven";
  if (currentTextVersion !== null && record.textVersion < currentTextVersion) return "Outdated";
  return "Current";
}

// --- Uploads carried in a request (PAT-26, PAT-71) — shared by consent (the signature) and patient documents ---------------

/** PAT-26: a document is at most 2 MB and 2000 px on its longer side; HEIC is converted client-side before upload. */
export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
export const MAX_UPLOAD_PX = 2000;
/**
 * What the Worker stores — the bytes are sniffed to one of these (BR-PATIENTS-012). A WebP or HEIC image is re-encoded as JPEG
 * on the device before it travels (BR-PATIENTS-031), so neither is ever declared here.
 */
export const UPLOAD_CONTENT_TYPES = ["image/jpeg", "image/png", "application/pdf"] as const;
export type UploadContentType = (typeof UPLOAD_CONTENT_TYPES)[number];
/** Standard base64 of MAX_UPLOAD_BYTES. */
export const MAX_UPLOAD_BASE64_LENGTH = Math.ceil(MAX_UPLOAD_BYTES / 3) * 4;

const BASE64_RE = /^[A-Za-z0-9+/]+={0,2}$/;
/** Standard base64 with no data-URL prefix and at least one byte (an empty or malformed payload is `malformed`, BR-GOVERNANCE-020). */
export const isStandardBase64 = (s: string): boolean => s.length > 0 && BASE64_RE.test(s);
const Base64 = z.string().max(MAX_UPLOAD_BASE64_LENGTH).refine(isStandardBase64, { message: "standard base64 without a data-URL prefix", params: { code: "malformed" } });
/** The decoded size of a base64 payload. */
export const base64DecodedBytes = (data: string): number => Math.floor((data.length * 3) / 4) - (data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0);

/** A file carried inline in a JSON body — a kiosk capture, a staff upload. The bytes go to R2, never D1. */
export const InlineUploadSchema = z.strictObject({
  contentType: z.enum(UPLOAD_CONTENT_TYPES),
  /** Standard base64 of the bytes, no data-URL prefix; at most 2 MB decoded. */
  data: Base64,
  fileName: z.string().trim().max(200).optional(),
}).refine((u) => base64DecodedBytes(u.data) <= MAX_UPLOAD_BYTES, { message: "at most 2 MB", path: ["data"], params: { code: "too_large" } });
export type InlineUpload = z.infer<typeof InlineUploadSchema>;
/** An inline upload, or a patient document already stored (by id) — e.g. a card scan captured a step earlier on the kiosk. */
export const UploadRefSchema = z.union([InlineUploadSchema, z.strictObject({ documentId: UlidSchema })]);
export type UploadRef = z.infer<typeof UploadRefSchema>;
export const isInlineUpload = (u: UploadRef): u is InlineUpload => "data" in u;
/**
 * The drawn signature (PAT-71, BR-GOVERNANCE-020): a PNG carried inline, at most 2 097 152 bytes decoded. The label is checked
 * here; the bytes (the PNG signature and the 2000 px edge) are checked by the server, which answers `unsupported_type` and
 * `too_many_pixels` on `signatureImage.data`.
 */
export const SignatureImageSchema = z.strictObject({
  contentType: z.literal("image/png"),
  data: Base64,
}).refine((u) => base64DecodedBytes(u.data) <= MAX_UPLOAD_BYTES, { message: "at most 2 097 152 bytes", path: ["data"], params: { code: "too_large" } });
export type SignatureImage = z.infer<typeof SignatureImageSchema>;
/** BR-GOVERNANCE-026: what the scan of a signed paper consent form may be — the patient documents' three kinds (PAT-26). */
export const FORM_SCAN_CONTENT_TYPES = ["image/jpeg", "image/png", "application/pdf"] as const;
export type FormScanContentType = (typeof FORM_SCAN_CONTENT_TYPES)[number];
/**
 * The scan of a signed paper consent form (BR-GOVERNANCE-026), carried inline in a `PaperScan` capture made at the desk: a JPEG,
 * PNG or PDF, at most 2 097 152 bytes decoded. The label is checked here; the bytes — the type they really are and, for an
 * image, 2000 px on its longest edge — by the server, which answers `unsupported_type` and `too_many_pixels` on `formScan.data`.
 */
export const FormScanSchema = z.strictObject({
  contentType: z.enum(FORM_SCAN_CONTENT_TYPES),
  data: Base64,
}).refine((u) => base64DecodedBytes(u.data) <= MAX_UPLOAD_BYTES, { message: "at most 2 097 152 bytes", path: ["data"], params: { code: "too_large" } });
export type FormScan = z.infer<typeof FormScanSchema>;

// --- Consent capture (UC-GOVERNANCE-013..016; PAT-71..PAT-75, INT-75e, TEN-64, TEN-65; D43, D58; BR-GOVERNANCE-019..025) ---

/**
 * The granular purposes (PAT-72 plus INT-75e's `ClinicalDataToCRM`), exactly as `consent_purpose.purpose` has stored them
 * since Sprint 0, in FIXTURE.md §7's display order ("six, same order everywhere"). §5.5's spellings (`Processing`,
 * `MedicalAidClaimSubmission`, `SupplierFulfilment`, `Photo`) are not used: the stored codes are the one vocabulary. D43: the
 * purposes a patient plainly expects are pre-ticked; `ClinicalDataToCRM` and `Marketing` are deliberate opt-ins;
 * `CareAndRecords` is required (PAT-72 (1)) and the only purpose that cannot be withdrawn (BR-GOVERNANCE-019).
 */
export const CONSENT_PURPOSES = ["CareAndRecords", "MedicalAidSharing", "SupplierSharing", "RemindersRecalls", "ClinicalDataToCRM", "Marketing"] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];
/** Stored on consent_record.consent_type: the kiosk's umbrella record is `Processing`; the others exist for a standalone consent. */
export const CONSENT_TYPES = ["Processing", "MarketingComms", "MedicalAidClaimSubmission", "Photo"] as const;
export type ConsentType = (typeof CONSENT_TYPES)[number];
/** How the consent was captured (PAT-71). §5.5 lists four; `PaperScan` is the staff-witnessed paper scan the schema also accepts. */
export const CONSENT_CHANNELS = ["InStoreTablet", "Online", "Paper", "PaperScan", "Verbal"] as const;
export type ConsentChannel = (typeof CONSENT_CHANNELS)[number];
/**
 * Where the patient signed (BR-GOVERNANCE-020, 12 Oct 2026), derived from the capture's source: `Kiosk` — a capture under a
 * handover token, or a kiosk registration's consent recorded at its approval; `Desk` — a capture in a staff member's own
 * session. `InStoreTablet` is both the kiosk and the desk's pad, so the channel alone cannot say which screen it was.
 */
export const CONSENT_CAPTURE_PLACES = ["Kiosk", "Desk"] as const;
export type ConsentCapturePlace = (typeof CONSENT_CAPTURE_PLACES)[number];
export const SIGNER_TYPES = ["Self", "Guardian"] as const;
export type SignerType = (typeof SIGNER_TYPES)[number];
/** The message channels the two per-channel purposes may name (PAT-72 (4) and (5)), in the order the record lists them. */
export const CONSENT_PURPOSE_CHANNELS = ["Email", "WhatsApp", "SMS"] as const;
export type ConsentPurposeChannel = (typeof CONSENT_PURPOSE_CHANNELS)[number];
export const CHANNEL_SCOPED_CONSENT_PURPOSES: readonly ConsentPurpose[] = ["RemindersRecalls", "Marketing"];
export const REQUIRED_CONSENT_PURPOSES: readonly ConsentPurpose[] = ["CareAndRecords"];
/** BR-GOVERNANCE-019: never pre-ticked by any configuration — POPIA s69 (direct marketing) and s26–27 (special personal information leaving the practice). */
export const OPT_IN_ONLY_CONSENT_PURPOSES: readonly ConsentPurpose[] = ["ClinicalDataToCRM", "Marketing"];
/** BR-GOVERNANCE-024: the purposes whose pre-ticked default the Owner may change. */
export const CONFIGURABLE_CONSENT_PURPOSES: readonly ConsentPurpose[] = ["MedicalAidSharing", "SupplierSharing", "RemindersRecalls"];
/** BR-GOVERNANCE-020: the guardian's relationship to the patient (PAT-73). */
export const GUARDIAN_RELATIONSHIPS = ["Mother", "Father", "LegalGuardian", "Grandparent", "Other"] as const;
export type GuardianRelationship = (typeof GUARDIAN_RELATIONSHIPS)[number];
/** BR-GOVERNANCE-020: the channels on which the signature is required (the pad); optional for a filed form or a witnessed verbal consent. */
export const SIGNATURE_REQUIRED_CHANNELS: readonly ConsentChannel[] = ["InStoreTablet", "Online"];
/** PAT-73: a patient under this age at signing is signed for by a guardian. */
export const CONSENT_ADULT_AGE = 18;
export const CONSENT_DESCRIPTION_MAX_LENGTH = 500;
export const CONSENT_WITHDRAWAL_REASON_MAX_LENGTH = 500;
export const CONSENT_SIGNER_NAME_MAX_LENGTH = 200;
export const CONSENT_DEVICE_MAX_LENGTH = 120;
/** How a consent record reads against the organisation's current consent text (BR-GOVERNANCE-021). */
export const CONSENT_RECORD_STATUSES = ["Current", "Outdated", "Withdrawn"] as const;
export type ConsentRecordStatus = (typeof CONSENT_RECORD_STATUSES)[number];
/** A patient's consent standing — the latest record's status, or `None` when there is no record (BR-GOVERNANCE-021, BR-15). */
export const PATIENT_CONSENT_STANDINGS = ["None", "Current", "Outdated", "Withdrawn"] as const;
export type PatientConsentStanding = (typeof PATIENT_CONSENT_STANDINGS)[number];
/** Where the privacy-contact email the form names comes from (BR-GOVERNANCE-024). */
export const PRIVACY_CONTACT_SOURCES = ["setting", "informationOfficer", "none"] as const;
export type PrivacyContactSource = (typeof PRIVACY_CONTACT_SOURCES)[number];
export const CONSENT_DESCRIPTION_SOURCES = ["platform", "tenant"] as const;
/** The audit `entityType` and outbox topic of a consent write (§4.1). */
export const CONSENT_RECORD_ENTITY = "consent_record";
export const CONSENT_CHANGED_TOPIC = "patient.consent_changed";
/**
 * Column classification of `consent_record` for the audit differ, the exports and the sealed files (BR-GOVERNANCE-020, TEN-72):
 * the guardian's ID number is identity — dropped when the entry is written, nulled and named for every reader.
 */
export const CONSENT_AUDIT_CLASSIFICATION: Readonly<Record<string, SpiClass>> = { guardian_id_number: "identity" };

/** The two placeholders a description or a consent text may carry; filled on the form read and the PDF, kept in the stored version (BR-GOVERNANCE-024). */
export function renderConsentPlaceholders(text: string, practiceName: string, privacyContactEmail: string | null): string {
  return text.replace(/\{practice\}/g, practiceName).replace(/\{privacyContactEmail\}/g, privacyContactEmail ?? "");
}

export interface ConsentPurposeDefinition {
  readonly purpose: ConsentPurpose;
  /** FIXTURE.md §7 wording, in display order. */
  readonly label: string;
  /** DVN's wording (D58) split as D43 requires (BR-GOVERNANCE-019); `{practice}` is the organisation's trading name, else its legal name. */
  readonly description: string;
  /** PAT-72 (1): the form cannot be submitted without it. */
  readonly required: boolean;
  /** D43: ticked when the consent form opens (the Owner may change the configurable ones). */
  readonly preTicked: boolean;
  /** May be answered per channel (Email, WhatsApp, SMS). */
  readonly channelScoped: boolean;
  /** Every purpose but `CareAndRecords` (BR-GOVERNANCE-022). */
  readonly withdrawable: boolean;
  /** Whether the Owner may change the pre-ticked default (BR-GOVERNANCE-024). */
  readonly configurable: boolean;
}

/** BR-GOVERNANCE-019 in FIXTURE §7's order ("six, same order everywhere"). Every purpose appears exactly once. */
export const CONSENT_PURPOSE_DEFINITIONS: readonly ConsentPurposeDefinition[] = [
  { purpose: "CareAndRecords", label: "Care and records", description: "{practice} collects, records, stores and processes your personal information, including your contact details, medical aid information and eye health records, for your examination, treatment, the supply of optical products, account administration and the other healthcare services provided to you.", required: true, preTicked: true, channelScoped: false, withdrawable: false, configurable: false },
  { purpose: "MedicalAidSharing", label: "Sharing with your medical aid for claims", description: "Where reasonably necessary, {practice} shares your personal information with medical aid schemes and administrators for your claims.", required: false, preTicked: true, channelScoped: false, withdrawable: true, configurable: true },
  { purpose: "SupplierSharing", label: "Sharing with suppliers and labs to fulfil orders", description: "Where reasonably necessary, {practice} shares your personal information with healthcare professionals involved in your care, laboratories, suppliers and other service providers assisting in the provision of services to you.", required: false, preTicked: true, channelScoped: false, withdrawable: true, configurable: true },
  { purpose: "RemindersRecalls", label: "Appointment reminders by WhatsApp / SMS", description: "Appointment reminders and other communications relating to your care by SMS, email or WhatsApp.", required: false, preTicked: true, channelScoped: true, withdrawable: true, configurable: true },
  { purpose: "ClinicalDataToCRM", label: "Clinical data to the practice's CRM", description: "Your prescriptions and visit history are kept in {practice}'s customer-relationship system so the practice can manage your orders and follow-ups.", required: false, preTicked: false, channelScoped: false, withdrawable: true, configurable: false },
  { purpose: "Marketing", label: "Marketing and offers", description: "Information regarding promotions, special offers, new products and services offered by {practice}.", required: false, preTicked: false, channelScoped: true, withdrawable: true, configurable: false },
];
export const CONSENT_PURPOSE_ORDER: readonly ConsentPurpose[] = CONSENT_PURPOSE_DEFINITIONS.map((d) => d.purpose);
export const CONSENT_PURPOSE_LABELS: Readonly<Record<ConsentPurpose, string>> = Object.fromEntries(CONSENT_PURPOSE_DEFINITIONS.map((d) => [d.purpose, d.label])) as Record<ConsentPurpose, string>;
export function consentPurposeDefinition(purpose: ConsentPurpose): ConsentPurposeDefinition {
  return CONSENT_PURPOSE_DEFINITIONS.find((d) => d.purpose === purpose)!;
}
/** 1-based display position of a purpose (BR-GOVERNANCE-019's order). */
export const consentPurposeOrder = (purpose: ConsentPurpose): number => CONSENT_PURPOSE_ORDER.indexOf(purpose) + 1;
/** Sort key over (purpose order, channel order) — a channel-independent answer before the channel answers of the same purpose. */
export function compareConsentAnswers(a: { purpose: ConsentPurpose; channel?: ConsentPurposeChannel | null | undefined }, b: { purpose: ConsentPurpose; channel?: ConsentPurposeChannel | null | undefined }): number {
  const byPurpose = consentPurposeOrder(a.purpose) - consentPurposeOrder(b.purpose);
  if (byPurpose !== 0) return byPurpose;
  const rank = (c: ConsentPurposeChannel | null | undefined) => (c ? CONSENT_PURPOSE_CHANNELS.indexOf(c) + 1 : 0);
  return rank(a.channel) - rank(b.channel);
}

export const ConsentPurposeInputSchema = z.strictObject({
  purpose: z.enum(CONSENT_PURPOSES),
  /** Only on the channel-scoped purposes; absent = the purpose as a whole. */
  channel: z.enum(CONSENT_PURPOSE_CHANNELS).optional(),
  granted: z.boolean(),
});
export type ConsentPurposeInput = z.infer<typeof ConsentPurposeInputSchema>;

/** D43: what the kiosk consent screen starts from — the required and pre-ticked purposes granted, the opt-ins not. */
export function consentPurposeDefaults(): ConsentPurposeInput[] {
  return CONSENT_PURPOSE_DEFINITIONS.map((d) => ({ purpose: d.purpose, granted: d.preTicked }));
}

/**
 * PAT-73 / BR-GOVERNANCE-020: the guardian who signs for a minor — the relationship, an ID number (an SA ID of 13 digits
 * passing Luhn, or a passport number of 6–20 letters and digits) and, when the guardian is a patient of the practice, their
 * patient id. The guardian's name travels as `signerName`. The ID number is classified `identity` (CONSENT_AUDIT_CLASSIFICATION).
 */
export const GuardianBlockSchema = z.strictObject({
  relationship: z.enum(GUARDIAN_RELATIONSHIPS),
  idNumber: z.string().trim().min(1).max(32).superRefine((s, ctx) => {
    if (/^\d{13}$/.test(s)) {
      if (!isValidSaId(s)) ctx.addIssue({ code: "custom", message: "not a valid South African ID number", params: { code: "invalid_sa_id" } });
      return;
    }
    if (!/^[A-Za-z0-9]{6,20}$/.test(s)) ctx.addIssue({ code: "custom", message: "an SA ID of 13 digits or a passport number of 6 to 20 letters and digits", params: { code: "invalid" } });
  }),
  patientId: UlidSchema.optional(),
});
export type GuardianBlock = z.infer<typeof GuardianBlockSchema>;

/** The shape shared by `POST /patients/{id}/consents` and the kiosk's consent step (the step's channel is `InStoreTablet`, set by the server). */
export const ConsentInputShape = {
  /** One answer per purpose, or per named channel for the two channel purposes; every purpose answered (BR-GOVERNANCE-020). */
  purposes: z.array(ConsentPurposeInputSchema).min(1).max(40),
  /** The consent text version presented (UC-GOVERNANCE-008); anything but the current published one is 409 VERSION_CONFLICT. */
  textVersion: z.number().int().min(1),
  /** The name as signed: defaults to the patient's own name for `Self`, required for `Guardian`. */
  signerName: z.string().trim().min(1).max(CONSENT_SIGNER_NAME_MAX_LENGTH).optional(),
  signerType: z.enum(SIGNER_TYPES),
  guardian: GuardianBlockSchema.optional(),
  /** The drawn signature (PAT-71), a PNG inline; required on the pad channels, optional for a filed form or a verbal consent. */
  signatureImage: SignatureImageSchema.optional(),
  /** The device label the client sends (else the user agent is recorded). */
  device: z.string().trim().min(1).max(CONSENT_DEVICE_MAX_LENGTH).optional(),
  /** D58: the declaration bullets the signer acknowledged, as shown. */
  acknowledgedItems: z.array(z.string().trim().min(1).max(300)).max(40).optional(),
} as const;

export interface ConsentInputLike {
  purposes: ConsentPurposeInput[];
  signerType: SignerType;
  signerName?: string | undefined;
  guardian?: GuardianBlock | undefined;
  signatureImage?: SignatureImage | undefined;
  /** Absent on the kiosk step, which is always `InStoreTablet`. */
  channel?: ConsentChannel | undefined;
}

/**
 * The refinements of a consent body (BR-GOVERNANCE-020), on any object carrying the shape: every purpose once (`duplicate`),
 * every purpose present (`missing`, the message names them), `CareAndRecords` granted (`required_purpose`), a channel only on
 * a per-channel purpose (`not_channel_purpose`) and never mixed with a channel-independent answer (`mixed_channels`); a
 * Guardian signer names themself and carries the block, a Self signer carries none; the signature where the channel demands it.
 */
export function consentInputIssues(v: ConsentInputLike, ctx: z.RefinementCtx): void {
  if (Array.isArray(v.purposes)) {
    const seen = new Set<string>();
    const plain = new Set<ConsentPurpose>();
    const scoped = new Set<ConsentPurpose>();
    const present = new Set<ConsentPurpose>();
    v.purposes.forEach((p, i) => {
      if (!p || typeof p !== "object") return;
      present.add(p.purpose);
      const key = `${p.purpose}|${p.channel ?? ""}`;
      if (seen.has(key)) {
        ctx.addIssue({ code: "custom", path: ["purposes", i], message: `${p.purpose}${p.channel ? ` (${p.channel})` : ""} is answered more than once`, params: { code: "duplicate" } });
        return;
      }
      seen.add(key);
      if (p.channel !== undefined) {
        if (!CHANNEL_SCOPED_CONSENT_PURPOSES.includes(p.purpose)) {
          ctx.addIssue({ code: "custom", path: ["purposes", i, "channel"], message: `${p.purpose} is not answered per channel`, params: { code: "not_channel_purpose" } });
          return;
        }
        if (plain.has(p.purpose)) {
          ctx.addIssue({ code: "custom", path: ["purposes", i], message: `${p.purpose} mixes a channel-independent answer with channel answers`, params: { code: "mixed_channels" } });
          return;
        }
        scoped.add(p.purpose);
        return;
      }
      if (scoped.has(p.purpose)) {
        ctx.addIssue({ code: "custom", path: ["purposes", i], message: `${p.purpose} mixes a channel-independent answer with channel answers`, params: { code: "mixed_channels" } });
        return;
      }
      plain.add(p.purpose);
      if (REQUIRED_CONSENT_PURPOSES.includes(p.purpose) && !p.granted) {
        ctx.addIssue({ code: "custom", path: ["purposes", i, "granted"], message: `${CONSENT_PURPOSE_LABELS[p.purpose]} is required`, params: { code: "required_purpose" } });
      }
    });
    const missing = CONSENT_PURPOSES.filter((p) => !present.has(p));
    if (missing.length) ctx.addIssue({ code: "custom", path: ["purposes"], message: `every purpose is answered; missing: ${missing.join(", ")}`, params: { code: "missing", missing } });
  }
  if (v.signerType === "Guardian") {
    if (!v.signerName) ctx.addIssue({ code: "custom", path: ["signerName"], message: "the guardian's name, as signed, is required", params: { code: "required" } });
    if (!v.guardian) ctx.addIssue({ code: "custom", path: ["guardian"], message: "a guardian's relationship and ID number are required", params: { code: "required" } });
  }
  if (v.signerType === "Self" && v.guardian) ctx.addIssue({ code: "custom", path: ["guardian"], message: "a guardian block belongs to a Guardian signer", params: { code: "not_allowed" } });
  const needsSignature = v.channel === undefined || SIGNATURE_REQUIRED_CHANNELS.includes(v.channel);
  if (needsSignature && !v.signatureImage) ctx.addIssue({ code: "custom", path: ["signatureImage"], message: "the signature is required on this channel", params: { code: "required" } });
}

/** BR-GOVERNANCE-026: the one channel that carries a scanned paper form, and that carries no drawn signature beside it. */
export const SCANNED_FORM_CHANNEL: ConsentChannel = "PaperScan";

/**
 * BR-GOVERNANCE-026: a `PaperScan` capture carries the scan of the signed form (`formScan`, `required`) and no drawn signature
 * (`signatureImage`, `not_allowed` — the signature is on the form); a scan on any other channel is `not_allowed`. One capture files
 * one document of record.
 */
export function deskCaptureIssues(v: { channel: ConsentChannel; formScan?: FormScan | undefined; signatureImage?: SignatureImage | undefined }, ctx: z.RefinementCtx): void {
  if (v.channel === SCANNED_FORM_CHANNEL) {
    if (!v.formScan) ctx.addIssue({ code: "custom", path: ["formScan"], message: "the scan of the signed paper form is required on this channel", params: { code: "required" } });
    if (v.signatureImage) ctx.addIssue({ code: "custom", path: ["signatureImage"], message: "a scanned paper form carries its signature; no drawn signature is sent with it", params: { code: "not_allowed" } });
  } else if (v.formScan) {
    ctx.addIssue({ code: "custom", path: ["formScan"], message: "a scan is sent only with a scanned paper form (PaperScan)", params: { code: "not_allowed" } });
  }
}

/**
 * POST /patients/{id}/consents (§5.5, UC-GOVERNANCE-013): ConsentInput → ConsentRecordView. The kiosk's capture is `InStoreTablet`;
 * the desk's may be any channel, and a `PaperScan` one carries the scan of the signed form (BR-GOVERNANCE-026).
 */
export const ConsentInputSchema = z.strictObject({ ...ConsentInputShape, channel: z.enum(CONSENT_CHANNELS), formScan: FormScanSchema.optional() }).superRefine((v, ctx) => {
  consentInputIssues(v, ctx);
  deskCaptureIssues(v, ctx);
});
export type ConsentInput = z.infer<typeof ConsentInputSchema>;

/** POST /patients/{id}/consents/{consentId}/withdraw (UC-GOVERNANCE-014, BR-GOVERNANCE-022): the purposes and a reason. */
export const ConsentWithdrawInputSchema = z.strictObject({
  purposes: z.array(z.enum(CONSENT_PURPOSES)).min(1).max(40),
  reason: z.string().trim().max(CONSENT_WITHDRAWAL_REASON_MAX_LENGTH).optional(),
}).superRefine((v, ctx) => {
  if (!v.reason) ctx.addIssue({ code: "custom", path: ["reason"], message: "a reason is required", params: { code: "required" } });
  if (Array.isArray(v.purposes)) {
    v.purposes.forEach((p, i) => {
      if (CONSENT_PURPOSES.includes(p) && !consentPurposeDefinition(p).withdrawable) {
        ctx.addIssue({ code: "custom", path: ["purposes", i], message: `${CONSENT_PURPOSE_LABELS[p]} cannot be withdrawn; an objection or erasure request stops it (UC-GOVERNANCE-009)`, params: { code: "not_withdrawable" } });
      }
    });
  }
});
export type ConsentWithdrawInput = z.infer<typeof ConsentWithdrawInputSchema>;

// --- Status derivation (BR-GOVERNANCE-021) -----------------------------------------------------------------------------------

/** A record's own status against the organisation's current consent text version: withdrawn wins over outdated. */
export function consentRecordStatus(record: { textVersion: number; withdrawnAt: string | null }, currentTextVersion: number | null): ConsentRecordStatus {
  if (record.withdrawnAt) return "Withdrawn";
  if (currentTextVersion !== null && record.textVersion < currentTextVersion) return "Outdated";
  return "Current";
}

/** The patient's standing from their latest non-voided record (newest signing time, then id): `None` without one. */
export function patientConsentStanding(latest: { textVersion: number; withdrawnAt: string | null } | null, currentTextVersion: number | null): PatientConsentStanding {
  return latest ? consentRecordStatus(latest, currentTextVersion) : "None";
}

// --- The consent form, the record and the tenant's consent settings (GET …/consents/form, GET …/consents, /organisation/consent-settings) --

/** One purpose as the kiosk and the staff capture render it: the definition with the tenant's defaults and the placeholders filled. */
export const ConsentFormPurposeSchema = z.object({
  order: z.number().int(),
  purpose: z.enum(CONSENT_PURPOSES),
  label: z.string(),
  description: z.string(),
  required: z.boolean(),
  preTicked: z.boolean(),
  perChannel: z.boolean(),
  withdrawable: z.boolean(),
  configurable: z.boolean(),
});
export type ConsentFormPurpose = z.infer<typeof ConsentFormPurposeSchema>;
/** The settings view adds where the description comes from and the stored template with its placeholders (for editing). */
export const ConsentSettingsPurposeSchema = ConsentFormPurposeSchema.extend({
  descriptionTemplate: z.string(),
  descriptionSource: z.enum(CONSENT_DESCRIPTION_SOURCES),
});
export type ConsentSettingsPurpose = z.infer<typeof ConsentSettingsPurposeSchema>;
export const PrivacyContactSchema = z.object({ value: z.string().nullable(), source: z.enum(PRIVACY_CONTACT_SOURCES) });
export type PrivacyContact = z.infer<typeof PrivacyContactSchema>;
/** A current privacy document as the form shows it — the body with the placeholders filled. */
export const ConsentTextRefSchema = z.object({ id: UlidSchema, version: z.number().int(), effectiveFrom: LocalDateSchema, publishedAt: InstantSchema.nullable(), bodyText: z.string() });
export const ConsentFormPatientSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  /** PAT-73: under 18 at the clock — a guardian signs; the default guardian is the main account holder. */
  isMinor: z.boolean(),
  guardianDefault: z.object({ patientId: z.string(), name: z.string(), relationship: z.string().nullable() }).nullable(),
  consentStatus: z.enum(PATIENT_CONSENT_STANDINGS),
  lastSigned: z.object({ textVersion: z.number().int(), signedAt: InstantSchema }).nullable(),
  /** When the status is Outdated: when the wording in force was published, so the kiosk can say so. */
  wordingChangedAt: InstantSchema.nullable(),
});
/** GET /patients/{id}/consents/form (UC-GOVERNANCE-015 A1) — reachable under a handover token: everything the consent step needs, nothing else. */
export const ConsentFormViewSchema = z.object({
  purposes: z.array(ConsentFormPurposeSchema),
  consentText: ConsentTextRefSchema.nullable(),
  privacyNotice: ConsentTextRefSchema.nullable(),
  privacyContactEmail: PrivacyContactSchema,
  practiceName: z.string(),
  patient: ConsentFormPatientSchema,
});
export type ConsentFormView = z.infer<typeof ConsentFormViewSchema>;

/** The Owner's stored configuration (BR-GOVERNANCE-024): overrides only, everything absent is the platform default. */
export interface ConsentSettingsStored {
  privacyContactEmail?: string | null | undefined;
  purposes?: Partial<Record<ConsentPurpose, { preTicked?: boolean | undefined; description?: string | undefined }>> | undefined;
  updatedAt?: string | undefined;
}

/**
 * PUT /organisation/consent-settings (UC-GOVERNANCE-016): the fields named are set, the rest keep their value. A description
 * of `null` or `""` restores the platform wording; a `privacyContactEmail` of `null` falls back to the Information Officer.
 */
export const ConsentSettingsSchema = z.strictObject({
  privacyContactEmail: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
  purposes: z.array(z.strictObject({
    purpose: z.enum(CONSENT_PURPOSES),
    preTicked: z.boolean().optional(),
    /** `{practice}` and `{privacyContactEmail}` are filled at render time. */
    description: z.string().trim().max(CONSENT_DESCRIPTION_MAX_LENGTH).nullable().optional(),
    /** Fixed per purpose; sent back unchanged is accepted, anything else is `fixed`. */
    required: z.boolean().optional(),
  })).max(CONSENT_PURPOSES.length).optional(),
}).superRefine((v, ctx) => {
  const seen = new Set<string>();
  (v.purposes ?? []).forEach((p, i) => {
    if (seen.has(p.purpose)) ctx.addIssue({ code: "custom", path: ["purposes", i], message: "a purpose appears more than once", params: { code: "duplicate" } });
    seen.add(p.purpose);
    const def = consentPurposeDefinition(p.purpose);
    if (p.required !== undefined && p.required !== def.required) ctx.addIssue({ code: "custom", path: ["purposes", i, "required"], message: `whether ${def.label} is required is fixed`, params: { code: "fixed" } });
    if (p.preTicked !== undefined) {
      if (def.required && !p.preTicked) ctx.addIssue({ code: "custom", path: ["purposes", i, "preTicked"], message: `${def.label} is required and always ticked`, params: { code: "fixed" } });
      else if (OPT_IN_ONLY_CONSENT_PURPOSES.includes(p.purpose) && p.preTicked) ctx.addIssue({ code: "custom", path: ["purposes", i, "preTicked"], message: `${def.label} is a deliberate opt-in and can never be pre-ticked (D43)`, params: { code: "never_pre_ticked" } });
    }
  });
});
export type ConsentSettings = z.infer<typeof ConsentSettingsSchema>;
export const ConsentSettingsViewSchema = z.object({
  purposes: z.array(ConsentSettingsPurposeSchema),
  privacyContactEmail: PrivacyContactSchema,
  updatedAt: InstantSchema.nullable(),
});
export type ConsentSettingsView = z.infer<typeof ConsentSettingsViewSchema>;

/**
 * The definitions with a tenant's settings applied (BR-GOVERNANCE-024): the required purpose stays ticked, an opt-in-only
 * purpose stays unticked whatever is stored, a stored description replaces the platform wording, and the placeholders are
 * filled with the practice's name and the privacy contact (an unset contact renders as an empty string).
 */
export function consentFormPurposes(stored: ConsentSettingsStored | null, practiceName: string, privacyContactEmail: string | null): ConsentSettingsPurpose[] {
  return CONSENT_PURPOSE_DEFINITIONS.map((d, i) => {
    const override = stored?.purposes?.[d.purpose];
    const template = override?.description ? override.description : d.description;
    const preTicked = d.required ? true : d.configurable && override?.preTicked !== undefined ? override.preTicked : d.preTicked;
    return {
      order: i + 1, purpose: d.purpose, label: d.label,
      description: renderConsentPlaceholders(template, practiceName, privacyContactEmail),
      descriptionTemplate: template, descriptionSource: override?.description ? "tenant" : "platform",
      required: d.required, preTicked, perChannel: d.channelScoped, withdrawable: d.withdrawable, configurable: d.configurable,
    };
  });
}

/** One answer of a record as read back — its state, when it last changed, by whom and why (a withdrawal's reason). */
export const ConsentPurposeStateSchema = z.object({
  purpose: z.enum(CONSENT_PURPOSES),
  channel: z.enum(CONSENT_PURPOSE_CHANNELS).nullable(),
  granted: z.boolean(),
  changedAt: InstantSchema,
  changedBy: UserRefSchema.nullable(),
  reason: z.string().nullable(),
});
export type ConsentPurposeState = z.infer<typeof ConsentPurposeStateSchema>;
/** The guardian as read back: the ID number is shown to the tenant roles and classified identity everywhere else (BR-GOVERNANCE-003). */
export const ConsentGuardianViewSchema = z.object({ relationship: z.string(), idNumber: z.string().nullable(), patientId: z.string().nullable() });
export const ConsentSignerViewSchema = z.object({ type: z.enum(SIGNER_TYPES), name: z.string().nullable(), guardian: ConsentGuardianViewSchema.nullable() });
/** The signature as the `PopiaConsent` document of the capture — opened through the patient documents route, never a storage key. */
export const ConsentDocumentRefSchema = z.object({ id: UlidSchema, type: z.literal("PopiaConsent"), contentType: z.string(), sizeBytes: z.number().int(), sha256: z.string() });
export const ConsentPdfRefSchema = z.object({ documentId: UlidSchema, sha256: z.string(), sizeBytes: z.number().int() });

/** A consent record as read back (UC-GOVERNANCE-015). Never the signature bytes, never a storage key. */
export const ConsentRecordViewSchema = z.object({
  id: UlidSchema,
  patientId: z.string(),
  textVersion: z.number().int(),
  /** Against the organisation's current consent text version (BR-GOVERNANCE-021). */
  status: z.enum(CONSENT_RECORD_STATUSES),
  /** The patient's latest record — the one a withdrawal may address (BR-GOVERNANCE-022). */
  isLatest: z.boolean(),
  channel: z.enum(CONSENT_CHANNELS),
  /** Where the patient signed — the kiosk or the desk (BR-GOVERNANCE-020). Additive (12 Oct 2026): absent in an older answer. */
  capturedOn: z.enum(CONSENT_CAPTURE_PLACES).nullable().default(null),
  /** The server's clock at capture, never the client's. */
  signedAt: InstantSchema,
  branch: BranchRefSchema.nullable(),
  signer: ConsentSignerViewSchema,
  /** The acting staff member, or the staff member who handed the device over. */
  capturedBy: UserRefSchema.nullable(),
  ip: z.string().nullable(),
  device: z.string().nullable(),
  /** In purpose order, then channel order; empty for a record that predates the purposes. */
  purposes: z.array(ConsentPurposeStateSchema),
  signatureDocument: ConsentDocumentRefSchema.nullable(),
  /**
   * The scan of the signed paper form a `PaperScan` capture filed as its `PopiaConsent` document (BR-GOVERNANCE-026), opened like the
   * signature through the patient documents route; null for any other capture. Additive (8 Oct 2026): absent in an older answer.
   */
  formDocument: ConsentDocumentRefSchema.nullable().default(null),
  /** The rendered consent PDF (BR-GOVERNANCE-025) — null until Browser Rendering is wired (Sprint 6). */
  pdf: ConsentPdfRefSchema.nullable(),
  withdrawnAt: InstantSchema.nullable(),
  withdrawnBy: UserRefSchema.nullable(),
  withdrawalReason: z.string().nullable(),
  createdAt: InstantSchema,
});
export type ConsentRecordView = z.infer<typeof ConsentRecordViewSchema>;

/** GET /patients/{id}/consents — newest first by signing time then id, keyset-paged (BR-GOVERNANCE-007). */
export const ConsentListQuerySchema = z.object({ limit: Limit, cursor: cursorSchema("consents").optional(), count: Flag.optional() });
export type ConsentListQuery = z.infer<typeof ConsentListQuerySchema>;

// --- The re-consent report (GET /re-consent-report; UC-GOVERNANCE-017 A4; BR-GOVERNANCE-027) -----------------------------------

/** The standings the re-consent report lists — every one but `Current` (BR-GOVERNANCE-021). */
export const RECONSENT_STANDINGS = ["None", "Outdated", "Withdrawn"] as const;
export type ReconsentStanding = (typeof RECONSENT_STANDINGS)[number];
/** Without `status`: the patients to ask at their next visit — no consent on record, or one on replaced wording (BR-GOVERNANCE-027). */
export const RECONSENT_DEFAULT_STANDINGS: readonly ReconsentStanding[] = ["None", "Outdated"];
/** BR-GOVERNANCE-027: one request reads at most this many patients, a batch at a time (one read of the batch's consent records each). */
export const RECONSENT_SCAN_LIMIT = 10_000;
export const RECONSENT_SCAN_BATCH = 95;
/** The counts walk the patients in id order, reading each batch's consent records by id range: two reads per batch. */
export const RECONSENT_COUNT_BATCH = 500;

const RECONSENT_CURSOR_TAG = "re-consent";
/** The report's keyset position — the patients' own order (surname, first names, id) of the last patient read — as an opaque cursor. */
export function encodeReconsentCursor(key: readonly [string, string, string]): string {
  return b64url(JSON.stringify([RECONSENT_CURSOR_TAG, ...key]));
}
/** The keyset position of a cursor the report issued, or null for anything else (a cursor of another collection included). */
export function decodeReconsentCursor(cursor: string): [string, string, string] | null {
  const text = fromB64url(cursor);
  if (text === null) return null;
  let parsed: unknown;
  try { parsed = JSON.parse(text); } catch { return null; }
  if (!Array.isArray(parsed) || parsed.length !== 4 || parsed[0] !== RECONSENT_CURSOR_TAG) return null;
  const [, surname, first, id] = parsed as unknown[];
  if (typeof surname !== "string" || typeof first !== "string" || typeof id !== "string" || id.length === 0) return null;
  return [surname, first, id];
}

export const ReconsentReportQuerySchema = z.object({
  /** One standing; without it, `None` and `Outdated` (RECONSENT_DEFAULT_STANDINGS). */
  status: z.enum(RECONSENT_STANDINGS).optional(),
  /** The patients' home branch; a branch of another organisation or of none is an empty page (BR-GOVERNANCE-008). */
  branchId: UlidSchema.optional(),
  limit: Limit,
  cursor: z.string().max(512).refine((c) => decodeReconsentCursor(c) !== null, { message: "cursor was not issued by the system for this collection", params: { code: "invalid_cursor" } }).optional(),
  /** The counts per standing (within the branch filter) and the total of the filtered set. */
  count: Flag.optional(),
});
export type ReconsentReportQuery = z.infer<typeof ReconsentReportQuerySchema>;

/** One patient of the report: who, where, and the record their standing comes from (never a clinical or identity field). */
export const ReconsentEntrySchema = z.object({
  patientId: UlidSchema,
  accountCode: z.string(),
  displayName: z.string(),
  homeBranchId: UlidSchema,
  patientStatus: z.enum(PATIENT_STATUSES),
  isDependant: z.boolean(),
  standing: z.enum(RECONSENT_STANDINGS),
  /** The latest non-voided record — the version, when and how it was signed; null when there is none. */
  lastSigned: z.object({
    consentId: UlidSchema, textVersion: z.number().int(), signedAt: InstantSchema, channel: z.enum(CONSENT_CHANNELS),
    /** Where it was signed (BR-GOVERNANCE-020). Additive (12 Oct 2026): absent in an older answer. */
    capturedOn: z.enum(CONSENT_CAPTURE_PLACES).nullable().default(null),
  }).nullable(),
  /** When the latest record's last withdrawable purpose was withdrawn (standing `Withdrawn`). */
  withdrawnAt: InstantSchema.nullable(),
});
export type ReconsentEntry = z.infer<typeof ReconsentEntrySchema>;
export const ReconsentCountsSchema = z.object({ None: z.number().int(), Outdated: z.number().int(), Withdrawn: z.number().int() });
export type ReconsentCounts = z.infer<typeof ReconsentCountsSchema>;
/** GET /re-consent-report: a keyset page of the report, the consent text in force and, with `?count=true`, the counts. */
export const ReconsentReportPageSchema = z.object({
  items: z.array(ReconsentEntrySchema),
  nextCursor: z.string().optional(),
  /** Only with `?count=true`: the patients of the filtered set (the standings asked for, the branch). */
  totalCount: z.number().int().optional(),
  /** Only with `?count=true`: the patients in each standing within the branch filter, whatever `status` asked for. */
  counts: ReconsentCountsSchema.optional(),
  /** The consent text in force, which an `Outdated` patient has not signed; null when none is published. */
  consentText: z.object({ version: z.number().int(), effectiveFrom: LocalDateSchema, publishedAt: InstantSchema.nullable() }).nullable(),
});
export type ReconsentReportPage = z.infer<typeof ReconsentReportPageSchema>;

/**
 * DVN's consent text (D58, BR-GOVERNANCE-024): the Patient Privacy Acknowledgement points 1–5 and the fourteen declaration
 * bullets, with `{practice}` for the practice's name and `{privacyContactEmail}` where the source has
 * `[practice POPIA contact email]`. Applied to DVN's tenant as a data step — the Owner publishes it as `ConsentText` v1
 * (UC-GOVERNANCE-008) — never as a platform default. The legal-entity line of the source ("Dynamic Vision Network (Pty) Ltd,
 * registration 1998/019372/07") is DVN's to add to the first sentence when publishing.
 */
export const DVN_CONSENT_TEXT = [
  "The Protection of Personal Information Act, 2013 (\"POPIA\") requires {practice} to inform patients how their personal information is collected, used, stored and protected.",
  "",
  "I acknowledge that:",
  "",
  "1. {practice} may collect, record, store and process my personal information, including my contact details, medical aid information and eye health records, for purposes related to my examination, treatment, the supply of optical products, account administration and other healthcare services provided to me.",
  "2. {practice} may, where reasonably necessary, share my personal information with medical aid schemes and administrators, healthcare professionals involved in my care, laboratories, suppliers and other service providers assisting in the provision of services to me.",
  "3. I have the right to access and request the correction of my personal information held by {practice} and may withdraw any consent given in terms of this document at any time by sending a written request to {practice} at {privacyContactEmail}.",
  "4. I understand that the withdrawal of my consent will not affect any processing undertaken before such withdrawal and that {practice} may retain certain records where required by law or professional obligations.",
  "5. {practice} may disclose my personal information where required or permitted by law or where necessary to protect or exercise a legal right.",
  "",
  "By signing this document, I declare that:",
  "",
  "- I have read and understood this Patient Registration, Professional Services, Financial Agreement and Terms and Conditions document.",
  "- I confirm that the personal and medical aid information I have provided is true and correct.",
  "- I consent to the professional eye examination and any clinically indicated procedures described in clause 2.",
  "- I understand that once I have approved my quotation and the laboratory has commenced manufacturing, my order cannot be cancelled or changed, as my lenses are individually manufactured to my prescription (clause 9).",
  "- I accept responsibility for all medical aid co-payments and shortfalls.",
  "- I understand that a minimum 50% deposit is required before my order is placed.",
  "- I understand that no products will be released to me until my account has been paid in full.",
  "- I understand that the re-use of my own frame, or a frame I supply, is solely at my own risk, as described in clause 11.",
  "- I understand that if my own frame becomes unusable after new lenses have been manufactured, I may be responsible for the cost of replacement lenses.",
  "- I understand that scratched lenses and damaged coatings cannot be polished or repaired.",
  "- I understand that alcohol, cleaning chemicals, heat and sun exposure may damage lens coatings and void the manufacturer's warranty.",
  "- I understand and accept the 24-hour appointment cancellation policy described in clause 21.",
  "- I consent to the collection, storage and processing of my personal information as described in clause 22 (POPIA).",
  "- I consent to receiving electronic communication as described in clause 23.",
  "- I voluntarily and willingly accept these Terms and Conditions in full.",
].join("\n");

// --- DSAR register (UC-GOVERNANCE-009, 010) ---------------------------------------------------------------------------------

export const DsarListQuerySchema = z.object({
  status: z.enum(DSAR_STATUSES).optional(),
  requestType: z.enum(DSAR_TYPES).optional(),
  patientId: OpaqueId.optional(),
  limit: Limit, cursor: cursorSchema("dsar-requests").optional(), count: Flag.optional(),
});
export type DsarListQuery = z.infer<typeof DsarListQuerySchema>;

export const DsarCreateSchema = z.strictObject({
  patientId: OpaqueId,
  requestType: z.enum(DSAR_TYPES),
  /** Never in the future (checked against the request clock). */
  requestedAt: InstantSchema,
  requestedVia: z.enum(DSAR_VIA),
  requestedByName: z.string().trim().min(1).max(200).nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  /** Defaults to the caller. */
  handledBy: UlidSchema.optional(),
});
export type DsarCreate = z.infer<typeof DsarCreateSchema>;

/** PATCH /dsar-requests — the collection route of §5.5; the request is named by `id` in the body. */
export const DsarDecisionSchema = z.strictObject({
  id: UlidSchema,
  status: z.enum(DSAR_STATUSES).optional(),
  refusalReason: z.string().trim().min(1).max(2000).nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  handledBy: UlidSchema.optional(),
});
export type DsarDecision = z.infer<typeof DsarDecisionSchema>;

/** BR-GOVERNANCE-012: the allowed status transitions; `Fulfilled` and `Refused` are final. */
export const DSAR_TRANSITIONS: Readonly<Record<DsarStatus, readonly DsarStatus[]>> = {
  Open: ["Fulfilled", "Refused", "Deferred"],
  Deferred: ["Fulfilled", "Refused"],
  Fulfilled: [],
  Refused: [],
};

/** The transitions allowed for a request of this type in Sprint 1: an Erasure is fulfilled only by the erasure itself (Sprint 2). */
export function dsarAllowedTransitions(from: DsarStatus, requestType: DsarType, erasureExecutionExists = false): readonly DsarStatus[] {
  const allowed = DSAR_TRANSITIONS[from];
  if (requestType === "Erasure" && !erasureExecutionExists) return allowed.filter((s) => s !== "Fulfilled");
  return allowed;
}

export type RetentionWord = number | "indefinite";

export const ErasureBasisSchema = z.object({
  mode: z.enum(["anonymisation_on_request", "time_lapse"]),
  retention: z.object({
    financialYears: z.union([z.number().int(), z.literal("indefinite")]),
    clinicalYears: z.union([z.number().int(), z.literal("indefinite")]),
    minorUntilAge: z.number().int(),
  }),
  inputs: z.object({
    lastFinancialDocumentDate: LocalDateSchema.nullable(),
    lastClinicalEntryDate: LocalDateSchema.nullable(),
    dateOfBirth: LocalDateSchema.nullable(),
  }).optional(),
  justification: z.string().optional(),
});
export type ErasureBasis = z.infer<typeof ErasureBasisSchema>;

/**
 * The retention basis in force for an Erasure request (UC-GOVERNANCE-009 A1, R-14): with any `indefinite` retention the
 * request is satisfied by anonymisation on request; otherwise the earliest permitted date follows from the inputs once known.
 */
export function erasureBasisOf(retention: { financialYears: RetentionWord; clinicalYears: RetentionWord; minorUntilAge: number }): ErasureBasis {
  if (retention.financialYears === "indefinite" || retention.clinicalYears === "indefinite") {
    return { mode: "anonymisation_on_request", retention, justification: "Records are retained indefinitely; an erasure request is satisfied by anonymising the patient's identifiers on request while the de-identified financial and clinical records are kept." };
  }
  return { mode: "time_lapse", retention, inputs: { lastFinancialDocumentDate: null, lastClinicalEntryDate: null, dateOfBirth: null } };
}

export const DsarRequestViewSchema = z.object({
  id: UlidSchema,
  patientId: z.string(),
  requestType: z.enum(DSAR_TYPES),
  requestedAt: InstantSchema,
  requestedVia: z.enum(DSAR_VIA).nullable(),
  requestedByName: z.string().nullable(),
  handledBy: UserRefSchema.nullable(),
  status: z.enum(DSAR_STATUSES),
  notes: z.string().nullable(),
  refusalReason: z.string().nullable(),
  earliestErasureDate: LocalDateSchema.nullable(),
  erasureBasis: ErasureBasisSchema.nullable(),
  /** `requestedAt` + 30 days — derived (POPIA/PAIA response period). */
  dueAt: InstantSchema,
  completedAt: InstantSchema.nullable(),
  executedAt: InstantSchema.nullable(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type DsarRequestView = z.infer<typeof DsarRequestViewSchema>;

// --- Diff redaction for readers (BR-GOVERNANCE-003) -------------------------------------------------------------------------

/**
 * Column classifications of audited entities whose diffs carry Special Personal Information. Keyed by audit `entityType` (the
 * table name), then by column name as it appears in the diff. Three enforcement points read it: the unit of work's default
 * redaction hook drops the `AUDIT_DROPPED_CLASSES` columns when the entry is written; every governance read and CSV export
 * passes a diff through it (the dropped classes for every reader, the reader's denied classes on top — spi.ts); the sealed
 * files pass each stored diff through it again. A module registers its entities when it loads, next to its table — the
 * patients module adds `patient` in Sprint 2 (adversarial review 16 Sep 2026, M2).
 */
export const AUDIT_DIFF_CLASSIFICATIONS: Record<string, Readonly<Record<string, SpiClass>>> = {};

export function registerAuditDiffClassification(entityType: string, fields: Readonly<Record<string, SpiClass>>): void {
  AUDIT_DIFF_CLASSIFICATIONS[entityType] = { ...(AUDIT_DIFF_CLASSIFICATIONS[entityType] ?? {}), ...fields };
}

/** The classifications no audit diff, CSV or sealed file ever carries, whoever reads them — TEN-72's set (BR-GOVERNANCE-003). */
export const AUDIT_DROPPED_CLASSES: readonly SpiClass[] = ["health", "identity"];

/** The columns of `entityType` whose registered classification is in {@link AUDIT_DROPPED_CLASSES}; empty for an unregistered entity. */
export function auditDroppedColumns(entityType: string): ReadonlySet<string> {
  const fields = AUDIT_DIFF_CLASSIFICATIONS[entityType];
  if (!fields) return EMPTY_COLUMNS;
  return new Set(Object.entries(fields).filter(([, cls]) => AUDIT_DROPPED_CLASSES.includes(cls)).map(([column]) => column));
}
const EMPTY_COLUMNS: ReadonlySet<string> = new Set();

/** The request-body names of classified fields, which a module's detail object may carry nested inside an audit image. */
const NESTED_SPI_KEYS: readonly string[] = ["idNumber", "medicalHistory", "medicalAidNumber", "membershipNumber", "dependentCode", "principalMember", "guardianIdNumber"];
const camel = (column: string): string => column.replace(/_([a-z0-9])/g, (_, ch: string) => ch.toUpperCase());

/**
 * Every key name that names a dropped-class field of ANY registered entity, as a column (`id_number`) and as its body name
 * (`idNumber`), plus the request-body names of the classified fields. The default redaction drops these wherever they sit
 * inside a nested object of an audit image — defence in depth for a detail a module attaches (BR-PATIENTS-016): the top
 * level is filtered per entity by {@link auditDroppedColumns}.
 */
export function auditDroppedKeyNames(): ReadonlySet<string> {
  const out = new Set<string>(NESTED_SPI_KEYS);
  for (const fields of Object.values(AUDIT_DIFF_CLASSIFICATIONS)) {
    for (const [column, cls] of Object.entries(fields)) if (AUDIT_DROPPED_CLASSES.includes(cls)) { out.add(column); out.add(camel(column)); }
  }
  return out;
}
